// Input Components - standalone versions for access-registration
import React, { useEffect, useRef } from 'react';
import { IMask, IMaskInput } from 'react-imask';
import MuiCheckbox, { CheckboxProps } from '@mui/material/Checkbox';
import MuiRadio, { RadioProps } from '@mui/material/Radio';
import { Palette } from './theme';
import { BorisScreener } from './borisScreenerTheme';

// ============ Masked Date of Birth Input ============

interface MaskedDateOfBirthFieldProps {
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const CURRENT_YEAR = new Date().getFullYear();

export const MaskedDateOfBirthInput: React.FC<MaskedDateOfBirthFieldProps> = (props) => {
  const { onChange, ...other } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.setAttribute('inputmode', 'numeric');
    }
  }, []);

  return (
    <IMaskInput
      {...other}
      mask="00/11/0000"
      definitions={{
        '#': /[0-9]/,
      }}
      blocks={{
        '00': {
          mask: IMask.MaskedRange,
          from: 1,
          to: 12,
          maxLength: 2,
        },
        '11': {
          mask: IMask.MaskedRange,
          from: 1,
          to: 31,
          maxLength: 2,
        },
        '0000': {
          mask: IMask.MaskedRange,
          from: 0,
          to: CURRENT_YEAR,
        },
      }}
      onAccept={(_, component) =>
        onChange?.({
          target: { name: props.name, value: component.value },
        } as React.ChangeEvent<HTMLInputElement>)
      }
      overwrite
      inputRef={inputRef}
    />
  );
};

// ============ Masked Phone Input ============

interface MaskedPhoneInputProps {
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  placeholder?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

export const MaskedPhoneInput: React.FC<MaskedPhoneInputProps> = (props) => {
  const { onChange, onBlur, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="(#00) 000-0000"
      definitions={{
        '#': /[0-9]/,
      }}
      onAccept={(_, component) =>
        onChange?.({
          target: { name: props.name, value: component.unmaskedValue },
        } as React.ChangeEvent<HTMLInputElement>)
      }
      onBlur={onBlur}
      overwrite
    />
  );
};

// ============ Masked Zip Input ============

interface MaskedZipInputProps {
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const MaskedZipInput: React.FC<MaskedZipInputProps> = (props) => {
  const { onChange, ...other } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.setAttribute('inputmode', 'numeric');
    }
  }, []);

  return (
    <IMaskInput
      {...other}
      mask="00000"
      definitions={{
        '#': /[0-9]/,
      }}
      onAccept={(_, component) =>
        onChange?.({
          target: { name: props.name, value: component.unmaskedValue },
        } as React.ChangeEvent<HTMLInputElement>)
      }
      overwrite
      inputRef={inputRef}
    />
  );
};

// ============ Checkbox ============

const SelectedCheckBoxIcon: React.FC<{ fillPath: string }> = ({ fillPath }) => (
  <svg fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
    <path
      d="M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z"
      fill={fillPath}
    />
    <path
      d="M6 13.1429L9.6 17L18 8"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UnselectedCheckboxIcon: React.FC<{ fillPath: string }> = ({ fillPath }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4 0C1.79086 0 0 1.79086 0 4V20C0 22.2091 1.79086 24 4 24H20C22.2091 24 24 22.2091 24 20V4C24 1.79086 22.2091 0 20 0H4ZM4 2C2.89543 2 2 2.89543 2 4V20C2 21.1046 2.89543 22 4 22H20C21.1046 22 22 21.1046 22 20V4C22 2.89543 21.1046 2 20 2H4Z"
      fill={fillPath}
    />
  </svg>
);

export const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  indeterminate,
  disabled,
  onChange = () => {},
  ...props
}) => {
  const checkboxColor = checked ? BorisScreener.coral : Palette.natural300;

  return (
    <MuiCheckbox
      checked={checked}
      indeterminate={indeterminate}
      disabled={disabled}
      checkedIcon={<SelectedCheckBoxIcon fillPath={checkboxColor} aria-hidden="true" />}
      icon={<UnselectedCheckboxIcon fillPath={checkboxColor} aria-hidden="true" />}
      onChange={onChange}
      sx={{
        padding: 0,
        height: '32px',
        width: '24px',
        '&:hover': { backgroundColor: 'transparent' },
        '&:focus': { backgroundColor: 'transparent' },
        '&:active': { backgroundColor: 'transparent', transition: 'none' },
      }}
      {...props}
    />
  );
};

// ============ RadioButton ============

const SelectedRadioButtonIcon: React.FC<{ fillPath: string }> = ({ fillPath }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="11" stroke={fillPath} strokeWidth="2" fill="none" />
    <circle cx="12" cy="12" r="6" fill={fillPath} />
  </svg>
);

const UnselectedRadioButtonIcon: React.FC<{ fillPath: string }> = ({ fillPath }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="11" stroke={fillPath} strokeWidth="2" fill="none" />
  </svg>
);

const radioButtonStyles = Object.freeze({
  unselected: {
    enabled: {
      color: Palette.natural300,
    },
    disabled: {
      color: Palette.natural100,
    },
  },
  selected: {
    enabled: {
      color: BorisScreener.coral,
    },
    disabled: {
      color: Palette.natural100,
    },
  },
});

const getRadioButtonColor = (variant: 'unselected' | 'selected', disabled?: boolean) => {
  const style = disabled
    ? radioButtonStyles[variant]?.disabled
    : radioButtonStyles[variant]?.enabled;
  return style?.color /* c8 ignore next */ || '';
};

export const RadioButton: React.FC<RadioProps> = ({
  checked,
  disabled,
  onChange = () => {},
  ...props
}) => {
  const variant = checked ? 'selected' : 'unselected';
  const radioButtonColor = getRadioButtonColor(variant, disabled);

  return (
    <MuiRadio
      checked={checked}
      disabled={disabled}
      checkedIcon={<SelectedRadioButtonIcon fillPath={radioButtonColor} aria-hidden="true" />}
      icon={<UnselectedRadioButtonIcon fillPath={radioButtonColor} aria-hidden="true" />}
      onChange={onChange}
      sx={{
        padding: 0,
        height: '32px',
        width: '24px',
        '&:hover': { backgroundColor: 'transparent' },
        '&:focus': { backgroundColor: 'transparent' },
        '&:active': { backgroundColor: 'transparent', transition: 'none' },
      }}
      {...props}
    />
  );
};
